import React, { useState } from "react";
import { Trash2, Eye, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { OrderReceipt } from "@/components/OrderReceipt";
import { updatePaymentStatus } from "@/lib/api";

const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

// Distinct color per spoke for instant visual ID.
const DEFAULT_SPOKE_COLORS = {
  Aylesbury:  { bg: "#DF513B", fg: "#FFFFFF" }, // brand red
  Whitehouse: { bg: "#2D5C8F", fg: "#FFFFFF" }, // blue
  Bletchley:  { bg: "#4F7355", fg: "#FFFFFF" }, // green
  Glebefarm:  { bg: "#E8B241", fg: "#1A1714" }, // amber
  Hatfield:   { bg: "#7E3C8A", fg: "#FFFFFF" }, // plum
  Uxbridge:   { bg: "#C44A6A", fg: "#FFFFFF" }, // rose
};

const SpokePill = ({ spoke, colors }) => {
  const c = (colors && colors[spoke]) || DEFAULT_SPOKE_COLORS[spoke] || { bg: "#1A1714", fg: "#FFFFFF" };
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest border-2 border-dotr-ink"
      style={{ background: c.bg, color: c.fg }}
      data-testid={`spoke-pill-${spoke}`}
    >
      {spoke}
    </span>
  );
};

const PAYMENT_OPTS = ["Pending", "Paid", "Paid cash", "Cancelled"];
const PAYMENT_STYLES = {
  Pending:      { bg: "#E8B241", fg: "#1A1714" }, // amber
  Paid:         { bg: "#4F7355", fg: "#FFFFFF" }, // green
  "Paid cash":  { bg: "#2D5C8F", fg: "#FFFFFF" }, // blue — distinct from card
  Cancelled:    { bg: "#1A1714", fg: "#FFFFFF" }, // ink
};

const PaymentCell = ({ order, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const current = order.payment_status || "Pending";
  const style = PAYMENT_STYLES[current] || PAYMENT_STYLES.Pending;

  const handleSelect = async (next) => {
    if (next === current) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      await updatePaymentStatus(order.id, next);
      toast.success(`Payment marked ${next}`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally {
      setBusy(false);
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <div className="flex flex-col gap-1" data-testid={`payment-edit-${order.id}`}>
        {PAYMENT_OPTS.map((opt) => {
          const s = PAYMENT_STYLES[opt];
          const active = opt === current;
          return (
            <button
              key={opt}
              type="button"
              disabled={busy}
              onClick={() => handleSelect(opt)}
              className={`inline-flex items-center justify-between gap-2 px-2 py-1 border-2 border-dotr-ink text-[10px] font-mono uppercase tracking-widest ${
                active ? "shadow-brutalSm" : "hover:translate-x-[1px] hover:translate-y-[1px]"
              }`}
              style={{ background: s.bg, color: s.fg }}
              data-testid={`payment-option-${order.id}-${opt}`}
            >
              {opt}
              {active && <span aria-hidden>✓</span>}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="font-mono text-[10px] text-dotr-inkmuted uppercase tracking-widest text-left mt-0.5 hover:text-dotr-ink"
        >
          cancel
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      disabled={busy}
      data-testid={`payment-status-${order.id}`}
      title="Click to change"
      className="inline-flex items-center gap-1 px-2 py-1 border-2 border-dotr-ink text-[10px] font-mono uppercase tracking-widest cursor-pointer hover:shadow-brutalSm hover:translate-x-[-1px] hover:translate-y-[-1px] transition-all"
      style={{ background: style.bg, color: style.fg }}
    >
      {busy && <Loader2 size={10} className="animate-spin" />}
      {current}
    </button>
  );
};

const Pill = ({ children, color = "ink" }) => {
  const map = {
    ink: "bg-dotr-ink text-white",
    red: "bg-dotr-primary text-white",
    yellow: "bg-dotr-yellow text-dotr-ink",
    green: "bg-dotr-green text-white",
    muted: "bg-dotr-muted text-dotr-ink border border-dotr-ink",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ${map[color]}`}
    >
      {children}
    </span>
  );
};

export const OrdersTable = ({ orders, onDelete, onChanged, onEdit, spokeColors }) => {
  const [viewing, setViewing] = useState(null);
  if (!orders.length) {
    return (
      <div className="brutal-card p-10 text-center" data-testid="orders-empty-state">
        <div className="font-heading text-3xl mb-2 uppercase">No orders yet</div>
        <p className="font-mono text-sm text-dotr-inkmuted">Submit one from the New Order tab — it will appear here instantly.</p>
      </div>
    );
  }

  return (
    <div className="brutal-card overflow-hidden" data-testid="orders-table-card">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse" data-testid="orders-table">
          <thead>
            <tr className="bg-dotr-muted border-b-2 border-dotr-ink">
              {[
                "Customer",
                "Contact",
                "Mode",
                "Location",
                "Meals",
                "Items",
                "Total",
                "Payment",
                "Requested",
                "Notes",
                "",
              ].map((h) => (
                <th
                  key={h}
                  className="text-left p-3 font-heading text-[11px] uppercase tracking-[0.12em] border-r border-dotr-ink last:border-r-0 whitespace-nowrap"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr
                key={o.id}
                className="border-b border-dotr-ink/30 hover:bg-dotr-bg transition-colors"
                data-testid={`order-row-${o.id}`}
              >
                <td className="p-3 align-top border-r border-dotr-ink/30">
                  <div className="font-heading font-semibold text-base">{o.customer_name}</div>
                  <div className="font-mono text-[10px] text-dotr-inkmuted">
                    {new Date(o.created_at).toLocaleString()}
                  </div>
                </td>
                <td className="p-3 align-top border-r border-dotr-ink/30">
                  <div className="font-mono text-xs">{o.email || "—"}</div>
                  <div className="font-mono text-xs text-dotr-inkmuted">{o.phone || "—"}</div>
                </td>
                <td className="p-3 align-top border-r border-dotr-ink/30">
                  <Pill color={o.pickup_delivery === "Delivery" ? "red" : "green"}>
                    {o.pickup_delivery}
                  </Pill>
                </td>
                <td className="p-3 align-top border-r border-dotr-ink/30">
                  <SpokePill spoke={o.spoke_location} colors={spokeColors} />
                </td>
                <td className="p-3 align-top border-r border-dotr-ink/30">
                  <div className="flex flex-wrap gap-1">
                    {o.lunch && <Pill color="yellow">Lunch</Pill>}
                    {o.dinner && <Pill color="muted">Dinner</Pill>}
                  </div>
                </td>
                <td className="p-3 align-top border-r border-dotr-ink/30 max-w-[280px]">
                  {(o.items && o.items.length > 0) ? (
                    <div className="flex flex-col gap-1">
                      {o.items.slice(0, 5).map((it, idx) => (
                        <div key={idx} className="flex items-center justify-between gap-2 font-mono text-[11px]">
                          <span className="truncate" title={it.name}>{it.name}</span>
                          <span className="font-bold text-dotr-primary">×{it.quantity}</span>
                        </div>
                      ))}
                      {o.items.length > 5 && (
                        <span className="font-mono text-[10px] text-dotr-inkmuted uppercase tracking-widest">
                          +{o.items.length - 5} more
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="font-mono text-xs text-dotr-inkmuted">—</span>
                  )}
                </td>
                <td className="p-3 align-top border-r border-dotr-ink/30 whitespace-nowrap">
                  {(typeof o.total_amount === "number" && o.total_amount > 0) ? (
                    <span className="font-heading font-semibold text-lg text-dotr-primary tracking-tight" data-testid={`order-total-${o.id}`}>
                      £{Number(o.total_amount).toFixed(2)}
                    </span>
                  ) : (
                    <span className="font-mono text-xs text-dotr-inkmuted">—</span>
                  )}
                </td>
                <td className="p-3 align-top border-r border-dotr-ink/30">
                  <PaymentCell order={o} onChanged={onChanged} />
                </td>
                <td className="p-3 align-top border-r border-dotr-ink/30 font-mono text-xs whitespace-nowrap">
                  {formatDateTime(o.requested_at)}
                </td>
                <td className="p-3 align-top border-r border-dotr-ink/30 max-w-[220px]">
                  <div className="font-body text-xs text-dotr-ink line-clamp-3">
                    {o.special_instructions || "—"}
                  </div>
                </td>
                <td className="p-3 align-top text-right">
                  <div className="inline-flex gap-1">
                    <button
                      onClick={() => setViewing(o)}
                      className="inline-flex items-center justify-center w-9 h-9 border-2 border-dotr-ink bg-white hover:bg-dotr-ink hover:text-white transition-colors"
                      aria-label={`View order ${o.customer_name}`}
                      data-testid={`btn-view-${o.id}`}
                    >
                      <Eye size={14} />
                    </button>
                    {onEdit && (
                      <button
                        onClick={() => onEdit(o)}
                        className="inline-flex items-center justify-center w-9 h-9 border-2 border-dotr-ink bg-white hover:bg-dotr-yellow hover:text-dotr-ink transition-colors"
                        aria-label={`Edit order ${o.customer_name}`}
                        data-testid={`btn-edit-${o.id}`}
                      >
                        <Pencil size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => onDelete(o.id)}
                      className="inline-flex items-center justify-center w-9 h-9 border-2 border-dotr-ink bg-white hover:bg-dotr-primary hover:text-white transition-colors"
                      aria-label={`Delete order ${o.customer_name}`}
                      data-testid={`btn-delete-${o.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {viewing && <OrderReceipt order={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
};
